"use client";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  blogOrProgramCard,
} from "@/customAnalytics/trackingEvents";
import { BlogCardReadMoreArrow } from "@/components/molecules/BlogCardReadMoreArrow";
import { POPULAR_BLOGS_1_WIDGET_DEFAULTS } from "@/constants/admin/fields/popularBlogs/popular_blogs_1";
import { cn } from "@/lib/utils";
import { Calendar } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  useMemo,
  useRef,
  type ReactNode,
} from "react";

/** Align with BlogList1 card chrome. */
const BLOG_CARD_DATE_TEXT = "#373737";
const BLOG_CARD_TITLE = "#101112";
const BLOG_CARD_DESC = "#101112";

interface PopularBlogMeta {
  id?: string;
  meta_cover_image?: string;
  meta_cover_alt?: string;
  meta_date_display?: string;
  meta_title?: string;
  /** Shown from `sm` only when present (mobile: hidden on main + side cards). */
  meta_description?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean | number | string;
}

const metaVisible = (item: PopularBlogMeta) =>
  item.meta_status === true ||
  item.meta_status === 1 ||
  item.meta_status === "1";

export default function PopularBlogs1({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const raw = (data as any)?.data ?? data;
  const sectionData = { ...POPULAR_BLOGS_1_WIDGET_DEFAULTS, ...raw };

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "popular_blogs_section");
  //   }
  // }, []);

  const {
    title,
    title_text_color,
    section_background_color,
    accent_color,
    item_divider_color,
    read_more_label,
    metadata,
  } = sectionData || {};

  const readMoreLabel = read_more_label || "Read More";
  const ctaColor = accent_color || "#60050e";

  const posts = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter(metaVisible)
      .sort(
        (a: PopularBlogMeta, b: PopularBlogMeta) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
      );
  }, [metadata]);

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!sectionData || !isActive || posts.length === 0) {
    return null;
  }

  const [featured, ...listRest] = posts;
  /** Figma: max three list items beside the featured card. */
  const listPosts = listRest.slice(0, 3);

  const renderRowLink = (
    href: string,
    post: PopularBlogMeta,
    index: number,
    children: ReactNode,
    className?: string
  ) => {
    const trimmed = href.trim();
    const wrapClass =
      className ??
      cn(
        "flex min-w-0 flex-1 items-stretch",
        "gap-[10px] min-[640px]:gap-[18px] min-[768px]:gap-5 min-[1024px]:gap-[22px]",
      );
    if (!trimmed) {
      return <span className={wrapClass}>{children}</span>;
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return (
        <a
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className={wrapClass}
        >
          {children}
        </a>
      );
    }
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return (
      <Link
        href={path}
        className={wrapClass}
        onClick={() =>
          blogOrProgramCard({
            section: "popular_blogs_section",
            component: "PopularBlogs1",
            label: String(post.meta_title ?? ""),
            position: index + 1,
            url: path,
          })
        }
      >
        {children}
      </Link>
    );
  };

  /** Featured read more — arrow sized to label at each breakpoint (Figma scale). */
  const featuredReadMore = () => {
    const arrowCls =
      "size-[14px] min-[640px]:size-[16px] min-[768px]:size-[17px] min-[1024px]:size-[18px]";
    const labelCls = cn(
      "text-center font-semibold leading-normal tracking-0",
      "text-[14px]",
      "min-[640px]:text-[16px]",
      "min-[768px]:text-[17px]",
      "min-[1024px]:text-[18px]",
    );
    return (
      <span
        className={cn(
          "inline-flex items-center transition-opacity",
          "gap-[6px] min-[768px]:gap-2 min-[1024px]:gap-2.5",
        )}
        style={{ color: ctaColor }}
      >
        <span className={labelCls}>{readMoreLabel}</span>
        <BlogCardReadMoreArrow className={arrowCls} />
      </span>
    );
  };

  /** List read more — arrow matches label px at each breakpoint. */
  const listReadMore = () => {
    const arrowCls = "size-[14px] min-[768px]:size-[15px] min-[1024px]:size-[16px]";
    const labelCls = cn(
      "font-semibold leading-normal tracking-0",
      "text-[14px]",
      "min-[640px]:text-[14px]",
      "min-[768px]:text-[15px]",
      "min-[1024px]:text-[16px]",
    );
    return (
      <span
        className={cn(
          "inline-flex items-center transition-opacity",
          "gap-[6px] min-[768px]:gap-2 min-[1024px]:gap-2",
        )}
        style={{ color: ctaColor }}
      >
        <span className={labelCls}>{readMoreLabel}</span>
        <BlogCardReadMoreArrow className={arrowCls} />
      </span>
    );
  };

  const rowInner = (post: PopularBlogMeta): ReactNode => {
    const desc = String(post.meta_description ?? "").trim();
    return (
      <>
        {post.meta_cover_image ? (
          <div
            className={cn(
              "relative shrink-0 self-start overflow-hidden bg-neutral-100",
              "h-[48px] w-[69px] rounded-[4px]",
              "min-[640px]:h-[134px] min-[640px]:w-[191px] min-[640px]:rounded-[10px]",
              "min-[768px]:h-[144px] min-[768px]:w-[205px]",
              "min-[1024px]:h-[153px] min-[1024px]:w-[218px]",
            )}
          >
            <Image
              src={post.meta_cover_image}
              alt={post.meta_cover_alt || ""}
              fill
              className="object-cover"
              sizes="(max-width: 1079px) 72px, 200px"
            />
          </div>
        ) : (
          <div
            className={cn(
              "shrink-0 self-start rounded-[4px] bg-neutral-100",
              "h-[48px] w-[69px]",
              "min-[640px]:h-[134px] min-[640px]:w-[191px] min-[640px]:rounded-[10px]",
              "min-[768px]:h-[144px] min-[768px]:w-[205px]",
              "min-[1024px]:h-[153px] min-[1024px]:w-[218px]",
            )}
            aria-hidden
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 flex-col gap-[7px]">
            <h3
              className={cn(
                "line-clamp-2 text-left font-bold tracking-0",
                "text-[14px] leading-snug",
                /* Figma 7749:17083: 16 Bold (sm) */
                "min-[640px]:line-clamp-3 min-[640px]:text-[16px] min-[640px]:leading-normal min-[640px]:font-bold",
                "min-[768px]:text-[18px]",
                "min-[1024px]:text-[20px]",
              )}
              style={{ color: BLOG_CARD_TITLE }}
            >
              {post.meta_title || ""}
            </h3>
            {desc ? (
              <p
                className={cn(
                  "hidden text-left leading-normal min-[640px]:block min-[640px]:line-clamp-2",
                  /* Mobile: hide (no description column). sm+: Figma 7749:17084 */
                  "text-[14px] font-normal",
                  "min-[640px]:text-[14px] min-[640px]:leading-normal",
                  "min-[768px]:text-[15px]",
                  "min-[1024px]:text-[16px]",
                )}
                style={{ color: BLOG_CARD_DESC }}
              >
                {desc}
              </p>
            ) : null}
          </div>
          <div className="mt-auto shrink-0 pt-2 min-[640px]:pt-3">{listReadMore()}</div>
        </div>
      </>
    );
  };

  /** Featured card typography: date 7749:17068, title 7749:17069, body 7749:17070, CTA 7749:17074 at sm; image heights unchanged. */
  const featuredInner = (post: PopularBlogMeta): ReactNode => {
    const desc = String(post.meta_description ?? "").trim();
    return (
      <>
        <div
          className={cn(
            "relative w-full shrink-0 overflow-hidden rounded-lg bg-neutral-100",
            "h-[148px]",
            "min-[640px]:h-[197px]",
            "min-[768px]:h-[218px]",
            "min-[1024px]:h-[238px]",
          )}
        >
          {post.meta_cover_image ? (
            <Image
              src={post.meta_cover_image}
              alt={post.meta_cover_alt || ""}
              fill
              className="object-cover"
              sizes="(max-width: 1079px) 100vw, 50vw"
              priority={false}
            />
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-col gap-2 min-[640px]:gap-1">
            {post.meta_date_display ? (
              <div className="flex min-w-0 items-center gap-2">
                <Calendar
                  className={cn(
                    "shrink-0",
                    "size-5 min-[640px]:size-6",
                    "min-[768px]:size-[26px]",
                    "min-[1024px]:size-[28px]",
                  )}
                  strokeWidth={1.75}
                  aria-hidden
                  style={{ color: BLOG_CARD_DATE_TEXT }}
                />
                <span
                  className={cn(
                    "truncate font-semibold leading-normal",
                    "text-[13px]",
                    "min-[640px]:text-[14px]",
                    "min-[768px]:text-[15px]",
                    "min-[1024px]:text-[16px]",
                  )}
                  style={{ color: BLOG_CARD_DATE_TEXT }}
                >
                  {post.meta_date_display}
                </span>
              </div>
            ) : null}
            <h3
              className={cn(
                "line-clamp-3 text-left font-semibold leading-normal tracking-0",
                "text-[18px]",
                /* Figma 7749:17069: 24 Semibold (sm) — H6-24/Semibold.600 */
                "min-[640px]:text-[24px]",
                "min-[768px]:text-[26px]",
                "min-[1024px]:text-[28px]",
              )}
              style={{ color: BLOG_CARD_TITLE }}
            >
              {post.meta_title || ""}
            </h3>
            {desc ? (
              <p
                className={cn(
                  "hidden text-left font-normal leading-normal",
                  "min-[640px]:block min-[640px]:line-clamp-3",
                  /* No description below sm on main card */
                  "min-[640px]:text-[18px]",
                  "min-[768px]:text-[19px]",
                  "min-[1024px]:text-[20px]",
                )}
                style={{ color: BLOG_CARD_DESC }}
              >
                {desc}
              </p>
            ) : null}
          </div>
          <div className="mt-auto shrink-0 pt-[22px]">{featuredReadMore()}</div>
        </div>
      </>
    );
  };

  const featuredHref = String(featured.meta_url ?? "");
  const hasSideList = listPosts.length > 0;

  return (
    <section
      ref={ref}
      className={cn(
        "w-full px-spacing-8 py-8",
        "min-[640px]:px-[36px] min-[640px]:py-[42px] min-[768px]:px-[44px] min-[768px]:py-[52px] min-[1024px]:px-[52px] min-[1024px]:py-[60px] min-[1280px]:px-[64px] min-[1280px]:py-[72px]",
      )}
      style={{ backgroundColor: section_background_color || "#FFFFFF" }}
    >
      <div className="mx-auto w-full max-w-[1750px]">
        <h2
          className="mb-5 text-left font-bold text-[22px] leading-tight min-[640px]:mb-6 min-[640px]:text-[24px] min-[768px]:text-[26px] min-[1024px]:text-[30px]"
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        <div
          className={cn(
            "flex flex-col gap-6",
            hasSideList && "min-[640px]:flex-row min-[640px]:items-stretch min-[640px]:gap-8",
          )}
        >
          <div
            className={cn(
              !hasSideList && "mx-auto w-full max-w-[543px]",
              hasSideList && "w-full min-w-0 min-[640px]:flex-1",
            )}
          >
            <article
              className="h-full overflow-hidden rounded-lg border border-gray-3 bg-[#f8f9fa] shadow-sm transition-shadow not-italic [font-family:var(--font-poppins),Poppins,sans-serif]"
              style={{ borderColor: item_divider_color }}
            >
              {renderRowLink(
                featuredHref,
                featured,
                0,
                featuredInner(featured),
                cn(
                  "flex h-full min-h-0 flex-col p-spacing-16",
                  "gap-3 transition-opacity hover:opacity-[0.98]",
                ),
              )}
            </article>
          </div>

          {hasSideList ? (
            <div className="min-h-0 w-full min-w-0 min-[640px]:flex-1">
              <ul className={cn("py-2 min-[640px]:px-0 min-[768px]:py-1 min-[1024px]:py-2")}>
                {listPosts.map((post: PopularBlogMeta, i: number) => {
                  const href = String(post.meta_url ?? "");
                  return (
                    <li
                      key={`${post.id ?? post.meta_title}-list-${i}`}
                      className="py-3 first:pt-0 last:pb-0 min-[640px]:py-4 min-[768px]:py-5"
                    >
                      {renderRowLink(href, post, i + 2, rowInner(post))}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
